/**
 * "Use production" on non-prod builds (ENG-2040, ENG-2297).
 *
 * redesign and dev are built against staging, whose database is a frozen copy
 * (ENG-1665). Staff can switch a non-prod build to the production API to test
 * the unreleased UI on live data. Since ENG-2297 (Jacob, 2026-10-08) that is
 * real production: every request goes to prod and admin / monitoring-api
 * permissions are the only gate, exactly as on the prod build. There is no
 * browser-side read-only guard any more; the earlier allowlist (ENG-2040) is
 * gone because QA needs to sync, arm and save on test sites from dev.
 *
 * Switching signs out and back in, because each environment's admin issues
 * its own session. Cognito is shared, so the round trip does not ask for a
 * password while the Cognito session is live. This module must not import
 * getApiUrl (getApiUrl imports it).
 */
export const PROD_API_URL = "https://admin.actuateui.net/";
export const PRODUCTION_MODE_KEY = "apiEnvironmentOverride";

// Both production APIs: US (.net) and EU (.eu). Missing the EU host made the
// EU prod build show the "Use production" pill, point it at the US API, and
// block live media as if it were a staging build.
const PROD_HOST = /(^|\/\/)admin\.actuateui\.(net|eu)(\/|$)/;

/** Built against something other than the production API. */
export const isNonProdBuild = (): boolean =>
  !PROD_HOST.test(process.env.API_URL ?? "");

export const isProductionModeOn = (): boolean => {
  if (!isNonProdBuild()) return false;
  try {
    return localStorage.getItem(PRODUCTION_MODE_KEY) === "prod";
  } catch {
    return false;
  }
};

/**
 * Live media (live view, listen-in, talk-down) can't work on this build: a
 * non-prod build (dev/redesign) on the staging API, which has no streaming
 * fleet and no real cameras, with Use production off. Never true on a
 * prod-config build. A build with no API_URL at all (unit tests) is left
 * alone so the players still run there.
 */
export const liveMediaNeedsProductionMode = (): boolean =>
  Boolean(process.env.API_URL) && isNonProdBuild() && !isProductionModeOn();

export const setProductionMode = (on: boolean): void => {
  try {
    if (on) localStorage.setItem(PRODUCTION_MODE_KEY, "prod");
    else localStorage.removeItem(PRODUCTION_MODE_KEY);
  } catch {
    // Storage blocked: the switch simply does not stick.
  }
};
